import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "react-toastify";

// Import SVG icons
import attendanceIcon from "../assets/attendance.svg";
import mailIcon from "../assets/mail.svg";
import csvIcon from "../assets/csv.svg";
import fileIcon from "../assets/file.svg";
import searchIcon from "../assets/search.svg";
import manageUser from "../assets/manageUser.svg";
import paymentReport from "../assets/paymentReport.svg";
import report from "../assets/data.svg";
import business from "../assets/business.svg";
import payout from "../assets/bill.svg";
import onboarding from "../assets/onboarding.svg"
import assignDeptIcon from "../assets/delegation.png";
import referralIcon from "../assets/referralIcon.png";
import campaignIcon from "../assets/bullhorn.png"

const View = () => {
  const navigate = useNavigate();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTab, setSearchTab] = useState("phone");  // "phone" | "emp"
  const [searchEmpCode, setSearchEmpCode] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isUTVMember, setIsUTVMember] = useState(false);
  const [featureAccess, setFeatureAccess] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [userId, setUserId] = useState("");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const handleViewAttendance = () => {
    navigate("/lkart/superadmin/view-attendance");
  };

  const handleSendMessage = () => {
    navigate("/lkart/superadmin/dashboard");
  };

  const handleUploadCSV = () => {
    navigate("/lkart/superadmin/upload-csv");
  };

  const hanldeRequestHistory = () => {
    navigate("/lkart/superadmin/request-history");
  };

  const handleManageUser = () => {
    navigate("/lkart/superadmin/manageUser");
  };

  const handleReferAndEarn = () => {
  navigate("/lkart/superadmin/refer-and-earn", { state: { userId } });
};

    const handlePayoutfile = () => {
    navigate("/lkart/superadmin/payout-listing");
  };

  const handleWorkerReport = () => {
    navigate("/lkart/superadmin/daily-report");
  };

  const handledepartmentautoassign=()=>{
    navigate("/lkart/superadmin/assign");
  }

  const handlePaymentReport = () => {
    navigate("/lkart/superadmin/payment-report");
  };

  const handleManageMetadata = () => {
    navigate("/lkart/superadmin/manage-metadata");
  };

  // Helper function to check if user has access to a feature
  const hasFeatureAccess = (featureName) => {
    // If super_admin, only check UTVMember status for Upload CSV
    if (userRole === "super_admin") {
      if (featureName === "Upload CSV") {
        return isUTVMember;
      }
      // All other features are accessible to super_admin
      return true;
    }

    // If Admin, only check featureAccess array (no UTVMember check)
    if (userRole === "Admin") {
      return featureAccess.includes(featureName);
    }

    // For any other role, deny access
    return false;
  };

  // Get all accessible features for layout
  const getAccessibleFeatures = () => {
    const features = [
      {
        name: "View Attendance",
        icon: attendanceIcon,
        handler: handleViewAttendance,
        condition: hasFeatureAccess("View Attendance"),
      },
      {
        name: "Send Message",
        icon: mailIcon,
        handler: handleSendMessage,
        condition: hasFeatureAccess("Send Message"),
      },
      {
        name: "Upload CSV",
        icon: csvIcon,
        handler: handleUploadCSV,
        condition: hasFeatureAccess("Upload CSV"),
      },
      {
        name: "View Request History",
        icon: fileIcon,
        handler: hanldeRequestHistory,
        condition: hasFeatureAccess("View Request History"),
      },
      {
        name: "Search Worker",
        icon: searchIcon,
        handler: () => setShowSearchModal(true),
        condition: hasFeatureAccess("Search Worker"),
      },
      {
  name: "Refer & Earn",
  icon: referralIcon, 
  handler: handleReferAndEarn,
  condition: true, 
},
      {
        name: "Manage Users",
        icon: manageUser,
        handler: handleManageUser,
        condition: hasFeatureAccess("Manage Users"),
      },
      {
        name: "Worker Report",
        icon: report,
        handler: handleWorkerReport,
        condition: true,
      },
      {
        name: "payment Report",
        icon: paymentReport,
        handler: handlePaymentReport,
        condition: (hasFeatureAccess("Send Payout Reports") && isUTVMember),
      },
      {
        name: "Manage Metadata",
        icon: business,
        handler: handleManageMetadata,
        condition: hasFeatureAccess("Manage Metadata"),
      },
      {
        name: "Payout file",
        icon: payout,
        handler: handlePayoutfile,
        condition: (
          ((
            hasFeatureAccess("Payout Generator") ||
            hasFeatureAccess("Payout Approver") ||
            hasFeatureAccess("Payout Accounts")
          )
          ) && isUTVMember
        ),
      },

       {
        name: "Assign Department",
        icon: assignDeptIcon,
        handler: handledepartmentautoassign,
        condition: hasFeatureAccess("Assign Department"),
       },
      {
        name: "Worker Onboarding",
        icon: onboarding,
        handler: () => navigate("/lkart/superadmin/worker-onboarding"),
        condition: hasFeatureAccess("Worker Verification"),
      },
      {
        name: "Manage Campaigns",
        icon: campaignIcon,
        handler: () => navigate("/lkart/superadmin/campaigns"),
        condition: hasFeatureAccess("Office Team"),
      },
    ];

    return features.filter((feature) => feature.condition);
  };

  const accessibleFeatures = getAccessibleFeatures();
  const featureCount = accessibleFeatures.length;

  // Determine grid layout based on number of features
  const getGridLayout = () => {
    switch (featureCount) {
      case 1:
        return "grid-cols-1 max-w-xs mx-auto";
      case 2:
        return "grid-cols-1 sm:grid-cols-2 max-w-xl mx-auto";
      case 3:
        return "grid-cols-1 sm:grid-cols-3 max-w-3xl mx-auto";
      case 4:
        return "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto";
      case 5:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 max-w-6xl mx-auto";
      case 6:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto";
      case 7:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto";
      case 8:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto";
      case 9:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto";
      default:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto";
    }
  };

  // Logout handler
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("superadmintoken");
      toast.success("Logged out successfully");
      navigate("/lkart/superadmin/");
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleSearchWorker = async () => {
    // Validation based on active tab
    if (searchTab === "phone") {
      if (!searchPhone.trim()) {
        toast.error("Please enter a phone number");
        return;
      }
      if (!validatePhoneNumber(searchPhone)) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
    } else {
      if (!searchEmpCode.trim()) {
        toast.error("Please enter an Worker code");
        return;
      }
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem("superadmintoken");

      // Build payload based on active tab
      const payload =
        searchTab === "phone"
          ? { phone: searchPhone.trim() }
          : { workerCode: searchEmpCode.trim() };

      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/search-worker`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("search worker", response);

      if (response.data.success && response.data.workerId) {
        const workerId = response.data.workerId;
        toast.success(response.data.message || "Worker found!");
        navigate(`/lkart/superadmin/attendance-history/${workerId}`);
        setShowSearchModal(false);
        setSearchPhone("");
        setSearchEmpCode("");
        setSearchTab("phone");
      } else {
        toast.error(
          searchTab === "phone"
            ? "No worker found for this phone number"
            : "No worker found for this worker code"
        );
      }
    } catch (error) {
      console.error("Search worker error:", error);

      if (error.response) {
        if (error.response.status === 404) {
          toast.error(
            searchTab === "phone"
              ? "No worker found for this phone number"
              : "No worker found for this worker code"
          );
        } else if (error.response.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } else if (error.request) {
        toast.error("Network error. Please try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSearching(false);
    }
  };

  const verifyTokenWithAuth = useCallback(
    async (authToken) => {
      try {
        const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response.data?.error?.name === "TokenExpiredError") {
          localStorage.removeItem("superadmintoken");
          return false;
        }

        if (response.data?.success && response.data?.user) {
          setIsUTVMember(response.data.user.UTVMember || false);
          setFeatureAccess(response.data.user.featureAccess || []);
          setUserRole(response.data.user.role || "");
          setUserId(response.data.user.id || response.data.user._id || "");
          console.log("User UTVMember status:", response.data.user.UTVMember);
          console.log("User role:", response.data.user.role);
          console.log("User feature access:", response.data.user.featureAccess);
        }

        return true;
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("superadmintoken");
        return false;
      }
    },
    [BASE_URL]
  );

  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = localStorage.getItem("superadmintoken");

      if (!token) {
        navigate("/lkart/superadmin/");
        return;
      }

      const isTokenValid = await verifyTokenWithAuth(token);

      if (!isTokenValid) {
        navigate("/lkart/superadmin/");
      }
    };

    checkTokenValidity();
  }, [navigate, verifyTokenWithAuth]);

  return (
    <>
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="w-full max-w-7xl bg-white shadow-lg rounded-lg p-6 sm:p-8">
          {/* Header */}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8 sm:mb-12">
            On-Demand Admin Dashboard
          </h1>

          {/* Dynamic Grid Layout */}
          {featureCount > 0 ? (
            <div className={`grid ${getGridLayout()} gap-4 sm:gap-6`}>
              {accessibleFeatures.map((feature, index) => {
                const isLastOfSeven = featureCount === 7 && index === 6;

                return (
                  <div
                    key={feature.name}
                    className={
                      isLastOfSeven
                        ? "col-span-full flex justify-center sm:col-start-2 sm:col-span-1"
                        : ""
                    }
                  >
                    <button
                      onClick={feature.handler}
                      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-4 sm:p-6 flex flex-col items-center justify-center space-y-3 border border-gray-200 hover:border-indigo-300 min-h-[120px] sm:min-h-[140px] w-full group"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-transform group-hover:scale-110">
                        <img
                          src={feature.icon}
                          alt={feature.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-sm sm:text-base font-semibold text-indigo-700 text-center">
                        {feature.name}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No features available for your role.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Search Worker</h3>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setSearchTab("phone")}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  searchTab === "phone"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Phone Number
              </button>
              <button
                onClick={() => setSearchTab("emp")}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  searchTab === "emp"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Worker Code
              </button>
            </div>

            {/* Phone Tab Panel */}
            {searchTab === "phone" && (
              <>
                <input
                  type="tel"
                  value={searchPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) setSearchPhone(value);
                  }}
                  placeholder="Enter phone number (e.g., 9998877445)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSearching}
                  maxLength={10}
                />
                <div className="text-sm text-gray-500 mb-4">
                  Enter 10-digit phone number without country code
                </div>
              </>
            )}

            {/* Worker Code Tab Panel */}
            {searchTab === "emp" && (
              <>
                <input
                  type="text"
                  value={searchEmpCode}
                  onChange={(e) => setSearchEmpCode(e.target.value)}
                  placeholder="Enter worker code (e.g., FAC0023343)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSearching}
                />
                <div className="text-sm text-gray-500 mb-4">
                  Enter the unique worker code assigned to the worker
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchPhone("");
                  setSearchEmpCode("");
                  setSearchTab("phone");
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={isSearching}
              >
                Cancel
              </button>
              <button
                onClick={handleSearchWorker}
                disabled={
                  isSearching ||
                  (searchTab === "phone" && (searchPhone.length !== 10)) ||
                  (searchTab === "emp" && !searchEmpCode.trim())
                }
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default View;